import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

export const CertificateCard: React.FC<{ cert: any; learnerName?: string }> = ({ cert, learnerName }) => {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const status = cert.effective_status || cert.status;
  const colors: Record<string, string> = {
    PASSED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    EXPIRED: "bg-amber-50 text-amber-700 border-amber-200",
    REVOKED: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div data-testid={`certificate-card-${cert.id}`}
      className="relative bg-[#FBF9F2] border-2 border-[#B99341] rounded-sm p-1.5 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="border border-[#B99341]/50 rounded-sm px-6 pt-5 pb-6" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
        <span className={`absolute top-4 right-4 text-[10px] font-mono-v font-semibold px-2 py-1 border rounded-md ${colors[status] || "bg-muted text-muted-foreground"}`}>
          {String(t(`cert.status.${status}`, status))}
        </span>
        <div className="flex flex-col items-center text-center">
          <img src="/brand/emblem-ceremonial.svg" alt="Vytali Academy" className="h-16 w-auto" />
          <p className="mt-2 text-[10px] tracking-[0.35em] text-[#B99341] font-semibold">VYTALI ACADEMY</p>
          <div className="mt-2 w-16 h-px bg-[#B99341]" />
          <h4 className="mt-3 text-lg font-semibold text-[#0A1F44] leading-snug">{fr ? cert.title_fr : cert.title_en}</h4>
          {learnerName && <p className="mt-1 text-sm italic text-[#0A1F44]/70">{learnerName}</p>}
        </div>
        <div className="mt-4 space-y-1 text-[11px] tracking-wide text-[#0A1F44]/60 text-center uppercase">
          <p>{t("cert.version")} — v{cert.version}</p>
          <p>{t("cert.earnedOn")} {cert.issued_at?.slice(0, 10)}</p>
          {cert.expires_at && <p>{t("cert.expiresOn")} {cert.expires_at.slice(0, 10)}</p>}
        </div>
        {cert.recertification_required && (
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs font-medium text-amber-700">
            <AlertTriangle size={13} /> {t("cert.recertRequired")}
          </p>
        )}
      </div>
    </div>
  );
};

export const ReadinessBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const map: Record<string, string> = {
    READY: "bg-emerald-600 text-white",
    IN_PROGRESS: "bg-[#002BF6] text-white",
    NOT_READY: "bg-zinc-200 text-zinc-700",
    UPDATE_REQUIRED: "bg-amber-500 text-white",
    BLOCKED: "bg-[#FF3B30] text-white",
  };
  return (
    <span data-testid={`readiness-badge-${status}`} className={`text-[10px] font-mono-v font-semibold px-2 py-1 rounded-md ${map[status] || "bg-muted"}`}>
      {String(t(`readiness.status.${status}`, status))}
    </span>
  );
};
